<script setup>
import _ from 'lodash';
import { computed, useAttrs } from 'vue';
import { useDisplay } from 'vuetify';

defineOptions({
    inheritAttrs: false,
});

const props = defineProps({
    type: {
        type: String,
        default: 'secondary',
    },
    label: String,
    forceLabel: Boolean,
    icon: String,
    color: {
        type: String,
        default: undefined,
    },
    kitIcon: String,
    loading: Boolean,
});

const emit = defineEmits(['click']);
const vuetifyDisplay = useDisplay();
const passthroughAttrs = useAttrs();

// Button hierarchy levels (#5828): primary = solid fill, secondary = tonal (outlined via vuetifyOverride.scss),
// tertiary = text. These and `icon` are the only types; the retired outlined/text types (and any other
// unknown value) fall back to secondary so a stray legacy call site never renders Vuetify outlined/text.
const levelVariants = {
    primary: 'flat',
    secondary: 'tonal',
    tertiary: 'text',
};
const level = computed(() => (levelVariants[props.type] ? props.type : props.type === 'icon' ? null : 'secondary'));

// An explicit color always wins; otherwise primary fills with the primary color and every other type
// keeps the secondary color the component has always defaulted to.
const resolvedColor = computed(() => props.color ?? (level.value === 'primary' ? 'primary' : 'secondary'));

// True whenever the button renders as a round icon-only button (explicit type="icon"
// or the xs breakpoint collapse). The text label can't fit inside a circular button,
// so it's exposed as a tooltip/accessible name instead of a visible span.
const isIconButton = computed(() => props.type === 'icon' || (vuetifyDisplay.xs.value && !props.forceLabel));
// When it's an icon button the label can't show; otherwise it always does (the only label-hiding
// case — xs without forceLabel — is already folded into isIconButton).
const showLabel = computed(() => !isIconButton.value);

const buttonAttributes = computed(() => {
    let attrs = _.clone(passthroughAttrs);
    if (level.value) {
        // The levels keep their variant even when collapsed to a round icon button at xs.
        attrs.variant = levelVariants[level.value];
    }
    if (isIconButton.value) {
        // type="icon" (and the xs collapse) renders a round icon-only button; icon forces no variant.
        attrs.icon = true;
        attrs.class = 'rounded-circle';
    }

    if (props.loading) {
        attrs.disabled = true;
    }

    return attrs;
});

const click = () => {
    if (props.loading) return;
    emit('click');
};
</script>

<template>
    <v-btn
        v-bind="buttonAttributes"
        :kit-icon="kitIcon"
        :color="resolvedColor"
        :data-cy="$attrs['data-cy']"
        :title="isIconButton ? label : undefined"
        :aria-label="isIconButton ? label : undefined"
        @click.stop="click"
    >
        <font-awesome-icon v-if="loading" :icon="['far', 'spinner']" size="lg" class="fa-spin" />
        <i v-else-if="kitIcon" :class="'mr-1 fa-kit ' + kitIcon" />
        <font-awesome-icon v-else-if="icon" :icon="['far', icon]" size="lg" :class="{ 'fa-spin': icon === 'spinner' }" />
        <span v-if="showLabel" :class="{ 'ml-2': label && icon }" style="margin-top: 2px">{{ label }}</span>
    </v-btn>
</template>
